import { Circle } from "@@/shared/circle";
import { ColorPicker } from "@@/shared/colors/color-picker";
import { Conditional } from "@@/shared/conditional";
import { VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import type { FieldId, FormId } from "@@/shared/form/form-and-field-id";
import { useFormId } from "@@/shared/form/form-id.context";
import { Inputs } from "@@/shared/form/standalones/form2/inputs";
import { useFormField } from "@@/shared/form/use-form-field";
import { Icon } from "@@/shared/icons/icon";
import { LayoutCell } from "@@/shared/layout-cell";
import { LayoutGrid } from "@@/shared/layout-grid";
import { TextBox } from "@@/shared/text";
import { FieldTitle } from "@@/shared/text/field-title";
import { useUpdateEffect } from "@@/shared/use-update-effect";
import { css, useTheme } from "@emotion/react";
import { faEye, faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import type {
    ColorItem,
    LinearGradient,
    RemSize,
    RequiredTranslatable,
} from "@towni/common";
import {
    getColorValue,
    gradientToCssValue,
    randomColor,
    translation,
} from "@towni/common";
import type { Draft } from "immer";
import { useState } from "react";
import type { ZodSchema } from "zod";

type Value = LinearGradient | undefined;

type Props<State> = {
    readonly className?: string;
    readonly fieldId: FieldId;
    readonly formId?: FormId;
    readonly getter: (state: Partial<State>) => Value;
    readonly setter: (state: Draft<Partial<State>>, newValue: Value) => void;
    readonly fieldSchema?: ZodSchema;

    readonly title: RequiredTranslatable;

    readonly preview?: boolean;
};

const Form2LinearGradient = <State extends Record<string, unknown>>(
    props: Props<State>,
) => {
    const theme = useTheme();
    const formIdFromContext = useFormId({ doNotThrow: true });

    const [previewSize, setPreviewSize] = useState(50);

    const formId = props.formId || formIdFromContext;
    const field = useFormField<State, Value>({
        fieldId: props.fieldId,
        getter: props.getter,
        setter: props.setter,
        fieldSchema: props.fieldSchema,
        formId: formId,
    });

    const [gradient, setGradient] = useState<LinearGradient>(
        field.initialValue ?? {
            _type: "LINEAR_GRADIENT",
            steps: [],
            degrees: 0,
        },
    );
    if (!field)
        throw new Error(`Field ${props.fieldId} in form ${formId} not found`);

    const setDegrees = (degrees: number) => {
        setGradient(g => {
            return {
                _type: g._type,
                degrees: degrees,
                steps: g.steps,
            };
        });
    };

    const addStep = (color: ColorItem, percentage: number) => {
        const c = getColorValue(color)?.asHex;
        if (!c) return;

        setGradient(g => {
            return {
                _type: g._type,
                degrees: g.degrees,
                steps: [...g.steps, { color: c, percentage: percentage }],
            };
        });
    };

    const updateStep = (
        index: number,
        color: ColorItem,
        percentage: number,
    ) => {
        const c = getColorValue(color)?.asHex;
        if (!c) return;
        setGradient(g => {
            const newSteps = [...g.steps];
            newSteps[index] = { color: c, percentage: percentage };
            return {
                _type: g._type,
                degrees: g.degrees,
                steps: newSteps,
            };
        });
    };

    useUpdateEffect(() => {
        field.setValue(gradient);
    }, [gradient]);

    return (
        <FlexColumn fillParentWidth>
            <FieldTitle text={props.title} />
            <VerticalDivider />
            <FlexColumn
                className={props.className}
                key={`${formId}_${props.fieldId}`}
                crossAxis="stretch"
                css={{
                    backgroundColor: theme.colors.textInput.background.asString,
                    padding: 20,
                    border: `1px solid ${theme.colors.textInput.border.asString}`,
                    borderRadius: 5,
                    //         borderBottom: `1px solid ${theme.colors.textInput.border.asString}`,
                }}>
                <Inputs.NumberInput
                    label={translation({
                        sv: "Vinkel",
                        en: "Angle",
                    })}
                    padding={{
                        all: 8,
                    }}
                    initialValue={gradient.degrees}
                    onChange={value => {
                        if (value === undefined) return;
                        setDegrees(value);
                    }}
                    postElement={() => <TextBox text="°" />}
                />
                <Conditional when={gradient.steps.length < 2}>
                    <VerticalDivider />
                    <TextBox
                        text={translation({
                            sv: "Lägg till minst två färg för att förhandsgranska",
                            en: "Add at least two color to preview",
                        })}
                        size={0.85 as RemSize}
                        weight="800"
                        color={theme.colors.default.text.value.withAlpha(0.5)}
                    />
                    <VerticalDivider />
                </Conditional>

                <Conditional when={props.preview && gradient.steps.length > 1}>
                    <VerticalDivider />
                    <FlexColumn
                        mainAxis="flex-end"
                        crossAxis="flex-end"
                        style={{
                            background: gradientToCssValue(gradient),
                        }}
                        css={{
                            width: "100%",
                            height: `${previewSize}px`,
                            borderRadius: 5,
                        }}>
                        <Circle
                            size={30}
                            styling={css`
                                min-width: ${15}px;
                                min-height: ${15}px;
                                margin-bottom: 10px;
                                margin-right: 10px;
                                cursor: pointer;
                                box-shadow: 0px 2px 10px 0px
                                    ${theme.colors.black.value.withAlpha(0.3)
                                        .asString};
                                border: 1px solid
                                    ${theme.colors.default.border.asString};
                            `}
                            onClick={event => {
                                event.stopPropagation();
                                setPreviewSize(s => (s === 50 ? 200 : 50));
                            }}
                            backgroundColor={theme.colors.default.background}>
                            <Icon
                                icon={faEye}
                                size={1 as RemSize}
                                color={theme.colors.disabled.text}
                            />
                        </Circle>
                    </FlexColumn>
                </Conditional>
                <VerticalDivider />
                <LayoutGrid>
                    <FlexRow mainAxis="space-between">
                        <TextBox
                            weight="700"
                            text={translation({
                                sv: "Steg",
                                en: "Steps",
                            })}
                        />

                        <Icon
                            icon={faPlus}
                            onClick={() => {
                                const lastStep =
                                    field.value?.steps[
                                        field.value.steps.length - 1
                                    ];
                                const f = lastStep ? lastStep.percentage : 0.1;

                                addStep(randomColor(), f + 0.1);
                            }}
                        />
                    </FlexRow>
                    <VerticalDivider L />
                    {gradient.steps.map((step, index) => (
                        <LayoutCell key={index}>
                            <LayoutGrid
                                css={{
                                    gridTemplateColumns: "100px 1fr auto",
                                    gap: 10,
                                    alignItems: "center",
                                }}>
                                <Inputs.NumberInput
                                    initialValue={step.percentage * 100}
                                    onChange={value => {
                                        if (value === undefined) return;
                                        updateStep(
                                            index,
                                            step.color,
                                            value / 100,
                                        );
                                    }}
                                    postElement={() => <TextBox text="%" />}
                                />
                                <ColorPicker
                                    // color={}

                                    color={getColorValue(step.color)?.asHex}
                                    onSelect={color => {
                                        updateStep(
                                            index,
                                            color,
                                            step.percentage,
                                        );
                                    }}
                                />
                                <Icon
                                    icon={faTrash}
                                    onClick={() => {
                                        setGradient(g => {
                                            const newSteps = [...g.steps];
                                            newSteps.splice(index, 1);
                                            return {
                                                _type: g._type,
                                                degrees: g.degrees,
                                                steps: newSteps,
                                            };
                                        });
                                    }}
                                />
                            </LayoutGrid>
                            <VerticalDivider />
                        </LayoutCell>
                    ))}
                </LayoutGrid>
            </FlexColumn>
        </FlexColumn>
    );
};

export { Form2LinearGradient };
